import React from 'react';

/**
 * GeoBull / GeoBear brand logo.
 * - Centered horizontal wordmark
 * - Bull / Bear emoji glyphs sit in subtle navy-bordered medallions
 * - Hairline gold rules flank the title to feel like a stamped brand mark
 * - Optional eyebrow micro-label above for brand reinforcement
 *
 * sizes:
 *   - "lg"  → page header (default)
 *   - "md"  → secondary placements
 */
const Logo = ({ size = 'lg', showEyebrow = true, className = '' }) => {
  const isLarge = size === 'lg';

  const titleSize = isLarge
    ? 'text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl'
    : 'text-xl sm:text-2xl md:text-3xl';

  const medallionSize = isLarge
    ? 'h-8 w-8 sm:h-11 sm:w-11 md:h-12 md:w-12 lg:h-14 lg:w-14'
    : 'h-7 w-7 sm:h-9 sm:w-9';
  const glyphSize = isLarge
    ? 'text-base sm:text-xl md:text-2xl lg:text-3xl'
    : 'text-sm sm:text-lg';
  const ruleWidth = isLarge ? 'w-12 md:w-16 lg:w-24' : 'w-8 sm:w-10';

  return (
    <div
      data-testid="brand-logo"
      role="img"
      aria-label="GeoBull / GeoBear"
      className={`flex flex-col items-center justify-center select-none ${className}`}
    >
      {/* Eyebrow micro-label */}
      {showEyebrow && (
        <span
          data-testid="brand-eyebrow"
          className="text-[10px] sm:text-xs font-medium tracking-[0.32em] uppercase mb-3"
          style={{ color: 'hsl(var(--gb-gold))' }}
        >
          Geopolitical Thesis Engine
        </span>
      )}

      {/* Wordmark row */}
      <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 flex-nowrap max-w-full">
        {/* Left rule */}
        <span
          aria-hidden="true"
          className={`hidden md:block h-px ${ruleWidth} flex-shrink-0`}
          style={{ backgroundColor: 'hsl(var(--gb-gold))' }}
        />

        <h1
          className={`${titleSize} font-semibold leading-none flex items-center gap-1.5 sm:gap-2 md:gap-3 whitespace-nowrap`}
          style={{
            fontFamily: 'var(--font-display)',
            color: 'hsl(var(--gb-navy))',
            letterSpacing: '-0.01em',
          }}
        >
          <span>GeoBull</span>

          {/* Bull medallion */}
          <span
            aria-hidden="true"
            className={`${medallionSize} ${glyphSize} inline-flex items-center justify-center rounded-full border shrink-0`}
            style={{
              borderColor: 'hsl(var(--gb-gold))',
              backgroundColor: 'hsl(var(--gb-surface))',
              boxShadow: '0 1px 2px hsl(var(--gb-navy) / 0.06)',
            }}
          >
            🐂
          </span>

          <span
            aria-hidden="true"
            className="font-light"
            style={{ color: 'hsl(var(--gb-gold))' }}
          >
            /
          </span>

          {/* Bear medallion */}
          <span
            aria-hidden="true"
            className={`${medallionSize} ${glyphSize} inline-flex items-center justify-center rounded-full border shrink-0`}
            style={{
              borderColor: 'hsl(var(--gb-gold))',
              backgroundColor: 'hsl(var(--gb-surface))',
              boxShadow: '0 1px 2px hsl(var(--gb-navy) / 0.06)',
            }}
          >
            🐻
          </span>

          <span>GeoBear</span>
        </h1>

        {/* Right rule */}
        <span
          aria-hidden="true"
          className={`hidden md:block h-px ${ruleWidth} flex-shrink-0`}
          style={{ backgroundColor: 'hsl(var(--gb-gold))' }}
        />
      </div>
    </div>
  );
};

export default Logo;
